import { useQuery } from '@tanstack/react-query';
import type { ApprovalDTO, ApprovalNoteDTO, AutomationGraph, AutomationRunDTO, GraphProblem, RestoreDTO, WebhookEndpointDTO } from '@prism/contracts';
import { api } from '../client';
import { queryKeys } from '../queryKeys';
import { usePaged } from './paged';

/** 1.6.2 "Who can run it": both empty means everyone who can run workflows. */
export interface RunAccess { groups: Array<{ id: string; name: string }>; roles: Array<{ key: string; name: string }> }

interface AutomationSummary {
  id: string; name: string; description: string; enabled: boolean; currentVersion: number | null;
  triggerType: string | null; runs24h: number; failed24h: number; active: number; updatedAt: string;
  runAccess: RunAccess; restricted: boolean; mayRun: boolean;
}

export interface AutomationDetail {
  id: string; name: string; description: string; enabled: boolean; currentVersion: number | null; maxConcurrentRuns: number;
  draft: AutomationGraph; draftRevision: number; draftChanged: boolean;
  webhookUrl: string; hasWebhookToken: boolean; problems: GraphProblem[]; runAccess: RunAccess; restricted: boolean; mayRun: boolean;
  versions: Array<{ version: number; triggerType: string; note: string | null; publishedAt: string; publishedBy: string | null }>;
}

export type RunDetail = AutomationRunDTO & {
  graph: AutomationGraph;
  approvals: Array<{
    id: string; nodeId: string; role: string | null; approverUserId: string | null; approverName: string | null; instructions: string; status: string;
    decidedBy: string | null; comment: string | null; decidedAt: string | null;
  }>;
};

type ApprovalItem = ApprovalDTO;

export function useRunnableWorkflows(subject: 'asset' | 'sequence', enabled = true) {
  return useQuery({
    queryKey: queryKeys.runnableWorkflows(subject),
    queryFn: () => api<{ items: Array<{ id: string; name: string; description: string; steps: string[] }> }>(`/api/v1/automations/runnable?subject=${subject}`),
    enabled,
    staleTime: 30_000,
  });
}

export function useAutomations() {
  return useQuery({ queryKey: queryKeys.automations(), queryFn: () => api<{ items: AutomationSummary[] }>('/api/v1/automations') });
}
export function useAutomation(id: string | undefined) {
  return useQuery({ queryKey: queryKeys.automation(id), queryFn: () => api<AutomationDetail>(`/api/v1/automations/${id}`), enabled: Boolean(id), staleTime: Infinity });
}
export function useAutomationRuns(filter: { definitionId?: string; status?: string } = {}) {
  const params = new URLSearchParams(Object.entries(filter).filter(([, v]) => v) as Array<[string, string]>);
  return usePaged<AutomationRunDTO>({ queryKey: queryKeys.automationRuns(params.toString()), url: `/api/v1/automation-runs?${params}`, refetchInterval: 5000 });
}
export function useRun(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.automationRun(id), queryFn: () => api<RunDetail>(`/api/v1/automation-runs/${id}`), enabled: Boolean(id),
    refetchInterval: (q) => (q.state.data && ['succeeded', 'failed', 'cancelled'].includes(q.state.data.status) ? false : 2500),
  });
}
interface ApprovalQuery { status?: 'pending' | 'approved' | 'rejected' | 'closed' | 'all'; scope?: 'all' | 'mine' | 'requested'; assetId?: string }
export function useApprovals(query: ApprovalQuery = {}, enabled = true) {
  const params = new URLSearchParams(Object.entries({ status: 'pending', ...query }).filter(([, v]) => v) as Array<[string, string]>);
  return usePaged<ApprovalItem>({ queryKey: queryKeys.approvals(params.toString()), url: `/api/v1/approvals?${params}`, refetchInterval: 10_000, enabled });
}
export function useApproval(id: string | null) {
  return useQuery({ queryKey: queryKeys.approval(id), queryFn: () => api<ApprovalItem>(`/api/v1/approvals/${id}`), enabled: Boolean(id) });
}
export function useApprovalNotes(id: string | null) {
  return useQuery({ queryKey: queryKeys.approvalNotes(id), queryFn: () => api<{ items: ApprovalNoteDTO[] }>(`/api/v1/approvals/${id}/notes`), enabled: Boolean(id), refetchInterval: 15_000 });
}
export function useWebhookEndpoints(enabled = true) {
  return useQuery({ queryKey: queryKeys.webhookEndpoints(), queryFn: () => api<{ items: WebhookEndpointDTO[] }>('/api/v1/webhook-endpoints'), enabled });
}
interface OperationsDTO {
  queues: Array<{ taskQueue: string; kind: string; pollers: number; backlog: number | null; lastPollAt: string | null; reportedAt: string; stale: boolean }>;
  /** Ingests of the last 24 hours, counted exactly as the Monitor Ingest tab counts them. */
  ingests: { active: number; done: number; failed: number; total: number };
  /** Processing runs (attempts and re-runs included), not files. */
  jobs: Record<'ingest' | 'transfers' | 'exports' | 'automations', Record<string, number>> & { restores?: Record<string, number>; qc?: Record<string, number> };
  /** Active jobs whose next step waits for a free worker (0.95.0). */
  waiting?: { ingest: number; transfers: number; exports: number };
  /** Restores from archive tiers (0.29.0): active ones and those of the last 24 hours. */
  restores?: RestoreDTO[];
  failures: Array<{ kind: 'ingest' | 'transfer' | 'export' | 'automation' | 'restore'; subjectId: string; subject: string; error: { message: string; code: string } | null; at: string | null; linkId: string }>;
  agents: Array<{ name: string; site: string; status: string; connected: boolean; lastSeenAt: string | null }>;
  storage: Array<{ name: string; kind: string; availability: string; bytes: number; files: number }>;
}
export function useOperations() {
  return useQuery({ queryKey: queryKeys.operations(), queryFn: () => api<OperationsDTO>('/api/v1/operations'), refetchInterval: 10_000 });
}
